import { useEffect } from 'react';
import { atom, useAtomValue } from 'jotai';
import { atomFamily, loadable } from 'jotai/utils';
import request from 'lib/request';

export interface ResourceState<T> {
  loading: boolean;
  data: T | null;
  error: boolean;
}

const LOADING: ResourceState<never> = {
  loading: true,
  data: null,
  error: false,
};
const FAILED: ResourceState<never> = {
  loading: false,
  data: null,
  error: true,
};
const EMPTY: ResourceState<never> = {
  loading: false,
  data: null,
  error: false,
};

type Loadable<T> =
  | { state: 'loading' }
  | { state: 'hasError'; error: unknown }
  | { state: 'hasData'; data: T };

const toResourceState = <T>(state: Loadable<unknown>): ResourceState<T> => {
  if (state.state === 'loading') return LOADING;
  if (state.state === 'hasError') return FAILED;
  return {
    loading: false,
    data: (state.data ?? null) as T | null,
    error: false,
  };
};

// Fetching lives in these atomFamilies rather than a useEffect so the data path
// matches the rest of the app. atomFamily keeps per-card state (keyed by request)
// without a shared module atom; loadable exposes loading/error. Cards evict their
// entry on unmount (below) so payloads don't accumulate for the session.

const cardGetAtomFamily = atomFamily((url: string) =>
  loadable(
    atom(async (): Promise<unknown> => {
      if (!url) return null;
      const res = await request.get<unknown>(url);
      if (res && typeof res === 'object' && 'error' in res) {
        throw new Error(String((res as { error: string }).error));
      }
      return res;
    })
  )
);

interface PostKey {
  url: string;
  bodyKey: string;
}
const postKey = (key: PostKey) => JSON.stringify([key.url, key.bodyKey]);

const cardPostAtomFamily = atomFamily(
  (key: PostKey) =>
    loadable(
      atom(async (): Promise<unknown> => {
        if (!key.url) return null;
        // request.post requires a controller; the atom has no unmount signal to
        // abort it, so an in-flight result is simply discarded when the card
        // unmounts and its family entry is removed.
        const res = await request.post<unknown, unknown>(
          key.url,
          JSON.parse(key.bodyKey),
          new AbortController()
        );
        if (res.type === 'success') return res.data;
        if (res.type === 'abort') return null;
        throw new Error('Request failed');
      })
    ),
  (a, b) => postKey(a) === postKey(b)
);

// Ref-count live consumers per family key so unmounting one card only evicts
// the atom once the last card sharing that key is gone — otherwise two cards on
// the same url would tear out each other's in-flight state.
const liveConsumers = new Map<string, number>();

const useFamilyEntry = (key: string, remove: () => void) => {
  useEffect(() => {
    liveConsumers.set(key, (liveConsumers.get(key) ?? 0) + 1);
    return () => {
      const next = (liveConsumers.get(key) ?? 1) - 1;
      if (next <= 0) {
        liveConsumers.delete(key);
        remove();
      } else {
        liveConsumers.set(key, next);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);
};

// Lazy GET fired when a card expands (the expand component mounts only then).
export const useCardGet = <T>(url: string): ResourceState<T> => {
  useFamilyEntry(url, () => cardGetAtomFamily.remove(url));

  const state = useAtomValue(cardGetAtomFamily(url));
  return url ? toResourceState<T>(state) : EMPTY;
};

// Lazy POST (RC line items query). Keyed by url + JSON-stable body.
export const useCardPost = <T>(
  url: string,
  body: unknown
): ResourceState<T> => {
  const bodyKey = JSON.stringify(body);

  useFamilyEntry(postKey({ url, bodyKey }), () =>
    cardPostAtomFamily.remove({ url, bodyKey })
  );

  const state = useAtomValue(cardPostAtomFamily({ url, bodyKey }));
  return url ? toResourceState<T>(state) : EMPTY;
};
